export type BlogCategory = 'renters-rights-act' | 'landlord-tips' | 'market-insights';

export interface BlogArticleContent {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  category: BlogCategory;
  author: string;
  readTimeMin: number;
  publishedAt: string;
  featured: boolean;
  sections: {
    heading?: string;
    body?: string;
    type?: 'callout' | 'warning' | 'tip' | 'list' | 'numbered-list' | 'cta' | 'cta-crosssell';
    items?: string[];
    ctaHeading?: string;
    ctaBody?: string;
    ctaLink?: string;
    ctaLabel?: string;
  }[];
  relatedSlugs?: string[];
  tags?: string[];
  seoTitle?: string;
  metaDescription?: string;
}

export const articles: BlogArticleContent[] = [
  {
    id: 'art-001',
    slug: 'renters-rights-act-does-not-go-far-enough',
    title: "Renters' Rights Act Does Not Go Far Enough, Says New Parliamentary Report",
    excerpt:
      "A cross-party parliamentary committee has concluded that the Renters' Rights Act, while welcome, falls short of fully protecting vulnerable tenants — and has issued seven key recommendations for the government.",
    category: 'renters-rights-act',
    author: 'PDA Legal Team',
    readTimeMin: 6,
    publishedAt: '2026-07-02T09:00:00Z',
    featured: true,
    tags: ['Renters Rights Act', 'Decent Homes Standard', "Awaab's Law", 'Local Housing Allowance', 'PRS Database'],
    seoTitle: "Is the Renters Rights Act Enough? | PDA Estates",
    relatedSlugs: [
      'section-8-selling-or-moving-in',
      'section-8-new-route-to-possession',
      'landlord-court-fees-rising-july-2026',
    ],
    sections: [
      {
        body: "The Housing, Communities and Local Government (HCLG) Select Committee has published a report concluding that the Renters' Rights Act, while a significant step forward, does not go far enough to protect the most vulnerable tenants in the private rented sector. The cross-party committee made seven substantive recommendations to the government, covering enforcement, property standards, housing benefit, and the new Private Rented Sector (PRS) Database.",
      },
      {
        heading: 'The Seven Key Recommendations',
        body: "The committee's report sets out a clear agenda for strengthening the Act's impact:",
        type: 'list',
        items: [
          'Introduce incentives for landlords to comply with the new Decent Homes Standard ahead of the 2035 deadline, rather than waiting for enforcement action.',
          "Roll out Awaab's Law — which requires landlords to fix damp and mould within fixed timeframes — to the private rented sector by the end of 2028/29.",
          'Conduct a full assessment of local authority resources available for PRS enforcement, given that only around £7 million of nearly £30 million in civil penalties imposed on landlords between 2023 and 2025 was actually collected.',
          'Develop the PRS Database to include landlord fitness and property quality data, not just registration information.',
          'Reverse the freeze on Local Housing Allowance (LHA) rates, restoring them to at least the 30th percentile of local market rents to help the most vulnerable tenants afford private rented accommodation.',
          'Make it easier for local councils to set up local licensing schemes for private landlords.',
          'Ensure proactive regulation and enforcement by local authorities, backed by a credible long-term housing strategy.',
        ],
      },
      {
        heading: "Florence Eshalomi MP: 'Proactive Regulation Is Essential'",
        body: "Committee chair Florence Eshalomi MP emphasised that the government must move beyond reactive enforcement. 'The private rented sector has too many properties that fall below acceptable standards, and too many tenants who lack the confidence or resources to challenge their landlord,' she said. 'The Renters' Rights Act gives us the framework — but without proper enforcement, resourcing, and a credible housing strategy, vulnerable tenants will continue to fall through the gaps.'",
      },
      {
        heading: 'Rent Controls: Off the Table',
        body: "The committee stopped short of recommending rent controls, concluding they are not a 'proportionate step' at this stage. The focus remains on enforcement, incentives for landlords, and improving housing benefit to make the private rented sector genuinely accessible to those on lower incomes.",
        type: 'callout',
      },
      {
        heading: "NRLA Response: Welcome Enforcement Focus, But No to Licensing",
        body: "The National Residential Landlords Association (NRLA) welcomed the committee's focus on enforcement, particularly the finding that civil penalties are rarely collected. However, NRLA CEO Ben Beadle rejected the recommendation to make local licensing schemes easier to establish, arguing that such schemes would duplicate data already being collected by the PRS Database and add unnecessary cost and bureaucracy for compliant landlords.",
      },
      {
        heading: 'What This Means for Chester Landlords',
        body: "For landlords in Chester and North Wales, the committee's report is a signal that regulatory pressure on the private rented sector will continue to increase. The Decent Homes Standard, once extended to private rentals, will require many landlords to invest in property upgrades. The PRS Database, when it goes live, will be a mandatory registration requirement — and failure to register will restrict your ability to use most Section 8 possession grounds.",
        type: 'tip',
      },
      {
        heading: 'Speak to Our In-House Solicitors',
        body: "PDA Letting Agents is Chester's only letting agency with an in-house landlord's solicitor. If you have questions about how the Renters' Rights Act affects your portfolio, or want to ensure your properties meet the new standards, contact us for a free consultation.",
      },
    ],
  },
  {
    id: 'art-002',
    slug: 'section-8-selling-or-moving-in',
    title: "Section 8 Explained: Selling Your Property or Moving In — Grounds 1 and 1a",
    excerpt:
      "With Section 21 abolished, landlords who want to sell their property or move a family member in must now use the new Section 8 Grounds 1 and 1a. Here's everything you need to know about notice periods, restrictions, and the serious penalties for misuse.",
    category: 'renters-rights-act',
    author: 'PDA Legal Team',
    readTimeMin: 8,
    publishedAt: '2026-07-02T10:00:00Z',
    featured: true,
    tags: ['Section 8', 'Ground 1', 'Ground 1a', 'Possession', 'Renters Rights Act', 'Form 3a'],
    seoTitle: "Section 8 Grounds 1 & 1a: Selling or Moving In | PDA Estates",
    relatedSlugs: [
      'section-8-new-route-to-possession',
      'renters-rights-act-does-not-go-far-enough',
      'landlord-court-fees-rising-july-2026',
    ],
    sections: [
      {
        body: "The abolition of Section 21 'no-fault' eviction under the Renters' Rights Act (in force from 1 May 2026) means that landlords who need to recover possession of their property must now rely on the revised Section 8 grounds. Two of the most important grounds for landlords are Ground 1 (moving in) and Ground 1a (selling the property). Both are mandatory grounds — meaning the court must grant possession if the ground is proven — but they come with strict rules, notice requirements, and serious penalties for misuse.",
      },
      {
        heading: 'Ground 1: Landlord or Close Family Member Wants to Move In',
        body: "Ground 1 allows a landlord to recover possession if they, or a close family member, genuinely intend to occupy the property as their only or principal home. Under the Renters' Rights Act, the definition of 'close family member' has been expanded and now includes:",
        type: 'list',
        items: [
          'Spouse or civil partner',
          'Parent or grandparent',
          'Sibling (including half-siblings)',
          'Child or grandchild',
        ],
      },
      {
        heading: 'Ground 1a: Landlord Intends to Sell the Property',
        body: "Ground 1a is a new ground introduced by the Renters' Rights Act. It allows a landlord to recover possession if they intend to sell the property. Crucially, the landlord does not need to have already listed the property or exchanged contracts — the intention to sell is sufficient, provided it is genuine and can be evidenced.",
      },
      {
        heading: 'Notice Requirements: The New Form 3a',
        body: "From 1 May 2026, all Section 8 notices must use the new Form 3a with the prescribed government wording. The old Form 3 is now invalid. Using the wrong form, or making errors in the prescribed wording, can result in fines. Key notice requirements for Grounds 1 and 1a:",
        type: 'list',
        items: [
          'Notice period: at least four months before applying for a possession order.',
          'The notice cannot expire within the first 12 months of the tenancy — you can serve the notice, but it must not take effect before the 12-month mark.',
          'Hand-deliver the notice where possible, and retain proof of service (an N215 form or signed endorsement).',
          'Do not combine Ground 1 or 1a with other grounds — this can raise questions about your intentions and complicate the court process.',
        ],
      },
      {
        heading: 'Restrictions That Can Block a Possession Order',
        body: "Even if you serve a valid notice and prove the ground, two restrictions can prevent the court from granting a possession order:",
        type: 'warning',
        items: [
          "Unprotected security deposit: if the tenant's deposit is not protected in a government-approved scheme, the court cannot grant possession.",
          'Not registered on the PRS Database: once the database goes live, failure to register will block most possession grounds including Grounds 1 and 1a.',
        ],
      },
      {
        heading: 'Rules on Re-Letting After Using Ground 1 or 1a',
        body: "If you recover possession using Ground 1 or 1a, you cannot re-let or advertise the property for re-letting for 12 months from the later of: (a) the date of the notice, or (b) the date you filed the court possession claim. This restriction is designed to prevent landlords from using these grounds as a backdoor eviction route.",
      },
      {
        heading: 'Penalties for Misuse: Up to £40,000',
        body: "The penalties for misusing Grounds 1 or 1a are severe:",
        type: 'warning',
        items: [
          'Civil fine of up to £7,000 if you had no genuine intention to sell or move in.',
          'Criminal fine of up to £40,000 for a deliberate breach.',
          'The tenant can apply for a rent repayment order covering up to two years of rent.',
        ],
      },
      {
        heading: 'Evidence You Will Need',
        body: "Courts are still developing their approach to evidence for Grounds 1 and 1a, but you should be prepared to demonstrate genuine intention. Useful evidence includes: estate agent instructions or property listings (for Ground 1a), school enrolment documents or evidence of the family member's current address (for Ground 1), and any correspondence confirming the decision to sell or move in.",
        type: 'tip',
      },
      {
        heading: 'Get Expert Advice Before Serving a Notice',
        body: "Given the complexity of the new Section 8 process and the serious penalties for errors, we strongly recommend taking legal advice before serving any Section 8 notice. PDA Letting Agents has an in-house landlord's solicitor who can guide you through the process and ensure your notice is correctly prepared and served.",
      },
    ],
  },
  {
    id: 'art-003',
    slug: 'section-8-new-route-to-possession',
    title: "Section 8: Your New Route to Possession After the Abolition of Section 21",
    excerpt:
      "Section 21 'no-fault' eviction was abolished on 1 May 2026. Every landlord in England now needs to understand the 37 revised Section 8 grounds — the only legal route to recovering possession of a rented property.",
    category: 'renters-rights-act',
    author: 'PDA Legal Team',
    readTimeMin: 7,
    publishedAt: '2026-06-25T09:00:00Z',
    featured: false,
    tags: ['Section 8', 'Section 21', 'Possession', 'Renters Rights Act', 'Eviction Grounds'],
    seoTitle: "Section 8 Possession After Section 21 Abolition | PDA Estates",
    relatedSlugs: [
      'section-8-selling-or-moving-in',
      'renters-rights-act-does-not-go-far-enough',
      'landlord-court-fees-rising-july-2026',
    ],
    sections: [
      {
        body: "On 1 May 2026, Section 21 of the Housing Act 1988 — the so-called 'no-fault eviction' route — was abolished in England. For the first time in nearly 40 years, landlords can no longer ask a tenant to leave without giving a legal reason. Every possession claim must now be made under Section 8, citing one or more of the 37 revised statutory grounds.",
      },
      {
        heading: 'Why Section 21 Was So Widely Used',
        body: "NRLA research found that 75% of landlords who had sought possession in recent years used Section 21. Crucially, the research also found that the most common underlying reasons were rent arrears (70% of cases) and anti-social behaviour (28%) — not a desire to evict without cause. Landlords used Section 21 because it was simpler and faster than the Section 8 process, not because they lacked a valid reason.",
      },
      {
        heading: 'The 37 Revised Section 8 Grounds',
        body: "The Renters' Rights Act has increased the number of Section 8 grounds from 17 to 37. They fall into two categories:",
        type: 'list',
        items: [
          'Mandatory grounds: the court must grant possession if the ground is proven. Examples include Ground 1a (intent to sell), Ground 8 (serious rent arrears of at least two months).',
          'Discretionary grounds: the court has discretion and can dismiss or suspend the order even if the ground is proven. Examples include Ground 10 (some rent arrears), Ground 12 (breach of tenancy obligations).',
          'Rarely used grounds also exist, such as Ground 5 (property required for a minister of religion) — these remain on the statute book but are seldom relevant in practice.',
        ],
      },
      {
        heading: 'The Court Waiting Time Problem',
        body: "The most significant practical challenge with the new Section 8 regime is court capacity. Under the old system, straightforward Section 21 cases could be resolved on paper without a hearing. Under Section 8, every possession claim requires a court hearing. Before the Act came into force, average waiting times for possession hearings were already around eight months.",
        type: 'warning',
      },
      {
        heading: "Government's Pledge on Court Digitisation",
        body: "Housing Minister Matthew Pennycook has pledged government funding to improve the justice system and deliver end-to-end digitisation of the possession court process by April or May 2027. The fee increases taking effect from 13 July 2026 (see our separate article on court fee changes) are partly intended to fund these improvements.",
      },
      {
        heading: 'What Landlords Need to Do Now',
        body: "If you have not already done so, you need to:",
        type: 'list',
        items: [
          'Familiarise yourself with the 37 Section 8 grounds and understand which are most likely to be relevant to your tenancies.',
          'Ensure all tenancy deposits are protected in a government-approved scheme — an unprotected deposit will block most possession grounds.',
          'Register on the PRS Database when it goes live — failure to register will also block possession grounds.',
          'Keep detailed records of all tenant communications, rent payments, and any maintenance issues — this evidence will be essential if you need to bring a possession claim.',
          'Use the new Form 3a for all Section 8 notices from 1 May 2026 — the old Form 3 is now invalid.',
        ],
      },
      {
        heading: 'PDA Can Help You Navigate the New Process',
        body: "The new Section 8 regime is significantly more complex than the old Section 21 process. PDA Letting Agents has an in-house landlord's solicitor who can advise you on the correct grounds to use, prepare and serve notices on your behalf, and represent you in possession proceedings if required. Contact us to discuss your situation.",
        type: 'tip',
      },
    ],
  },
  {
    id: 'art-004',
    slug: 'digital-right-to-rent-checks-october-2026',
    title: "New Digital Right to Rent Rules: What Landlords Must Do Before October 2026",
    excerpt:
      "From 1 October 2026, landlords conducting electronic Right to Rent checks must use a registered digital verification provider. Here's what the new rules mean, who is affected, and how to stay compliant.",
    category: 'landlord-tips',
    author: 'PDA Legal Team',
    readTimeMin: 6,
    publishedAt: '2026-07-02T11:00:00Z',
    featured: false,
    tags: ['Right to Rent', 'Digital Checks', 'Immigration', 'Compliance', 'eVisa'],
    relatedSlugs: [
      'right-to-rent-complete-guide',
      'section-8-new-route-to-possession',
      'renters-rights-act-does-not-go-far-enough',
    ],
    sections: [
      {
        body: "From 1 October 2026, new secondary legislation will make it mandatory for landlords conducting electronic Right to Rent checks to use a registered digital verification service provider (RtR DVSP). This is a significant change for any landlord or letting agent who currently uses their own digital verification process or an unregistered provider.",
      },
      {
        heading: 'Background: Right to Rent in England',
        body: "Right to Rent checks were introduced in England in 2014 under the Immigration Act. They require landlords to verify that all adult tenants (aged 18 and over) have the legal right to rent residential property in England before the tenancy begins. Failure to comply can result in a civil penalty of up to £20,000 per occupier, or a criminal conviction carrying an unlimited fine or up to five years in prison.",
      },
      {
        heading: 'The Three Ways to Conduct a Right to Rent Check',
        body: "From 1 October 2026, landlords will have three compliant options:",
        type: 'list',
        items: [
          'Manual check: examine original hard-copy documents in person (List A or List B documents from the Home Office code of practice). This option remains available and does not require a digital provider.',
          'Home Office online checking service: for tenants who hold a biometric residence permit or card, or who have a UKVI account (including eVisa holders). The tenant provides a share code and the landlord verifies online.',
          'Registered digital verification service provider (RtR DVSP): for electronic checks on British and Irish nationals using Identity Document Verification Technology (IDVT). From October 2026, only registered providers can be used for this type of check.',
        ],
      },
      {
        heading: 'The Move to eVisas',
        body: "The government has been transitioning to a fully digital immigration system since 2018. Physical biometric residence permits and cards are no longer being issued to new applicants. Instead, most non-UK nationals now hold an eVisa — a digital immigration status accessible via a UKVI account. For landlords, this means that for most non-UK tenants, the correct check is via the Home Office online service using a share code provided by the tenant.",
        type: 'callout',
      },
      {
        heading: 'Who Needs to Check?',
        body: "You must conduct a Right to Rent check on every adult (aged 18 or over) who will occupy the property as their only or main home, regardless of their nationality — including British and Irish nationals. Checking only non-UK nationals would be discriminatory under the Equality Act 2010.",
      },
      {
        heading: 'Exemptions from Right to Rent',
        body: "Not all lettings require a Right to Rent check. Exemptions include:",
        type: 'list',
        items: [
          'Holiday lets and short-term accommodation',
          "Lettings where the property is not the tenant's main home",
          'Tenancies of more than seven years with no landlord break clause',
          'Student accommodation arranged directly by an educational institution',
          'Employer-provided accommodation',
          'Mobile homes',
        ],
      },
      {
        heading: 'Follow-Up Checks for Time-Limited Rights',
        body: "If a tenant has a time-limited right to rent (for example, a visa that expires), you must conduct a follow-up check before their right to rent expires. A valid initial check provides a statutory excuse for at least 12 months, but you must diarise the follow-up date and act promptly.",
        type: 'warning',
      },
      {
        heading: 'Document Retention',
        body: "You must retain copies of all Right to Rent check documents for the duration of the tenancy and for at least one year after it ends. For digital checks, retain a record of the check date, the result, and the provider used.",
      },
      {
        heading: 'Action Required Before October 2026',
        body: "If you currently use any form of electronic Right to Rent check, review your process now. Ensure that from 1 October 2026 you are using a provider that is registered on the government's list of approved RtR DVSPs. PDA Letting Agents handles Right to Rent checks as part of our full management service — contact us if you need guidance.",
        type: 'tip',
      },
    ],
  },
  {
    id: 'art-005',
    slug: 'right-to-rent-complete-guide',
    title: "Right to Rent: The Complete Guide for Landlords in England (2026)",
    excerpt:
      "A comprehensive guide to Right to Rent checks for private landlords in England — covering who to check, how to check, eVisas, penalties, and the new digital rules coming into force in October 2026.",
    category: 'landlord-tips',
    author: 'PDA Legal Team',
    readTimeMin: 10,
    publishedAt: '2026-07-05T09:00:00Z',
    featured: false,
    tags: ['Right to Rent', 'Compliance', 'Immigration', 'eVisa', 'IDVT', 'Home Office'],
    relatedSlugs: [
      'digital-right-to-rent-checks-october-2026',
      'section-8-new-route-to-possession',
      'landlord-court-fees-rising-july-2026',
    ],
    sections: [
      {
        body: "Right to Rent is a legal requirement for all private landlords in England. Introduced under the Immigration Act 2014, it requires landlords to check that every adult tenant has the legal right to rent residential property in England before the tenancy begins. This guide covers everything you need to know — including the new digital rules coming into force on 1 October 2026.",
      },
      {
        heading: 'Who Is Responsible for Right to Rent Checks?',
        body: "The legal responsibility lies with the landlord. However, it can be transferred to a letting agent via a written agreement — if you use a managing agent, check your agreement to confirm whether they are conducting Right to Rent checks on your behalf. If you sublet, the subletting tenant is usually responsible for checking their subtenants.",
      },
      {
        heading: 'Who Must Be Checked?',
        body: "You must check every adult (aged 18 or over) who will occupy the property as their only or main home. This includes:",
        type: 'list',
        items: [
          'All named tenants on the tenancy agreement',
          'Any other adults who will live at the property, even if not on the tenancy agreement',
          'British and Irish nationals — you must check everyone to avoid discrimination under the Equality Act 2010',
          'You do not need to check children under 18',
        ],
      },
      {
        heading: 'Exemptions: When You Do Not Need to Check',
        body: "Right to Rent checks are not required for:",
        type: 'list',
        items: [
          'Holiday lets and short-term accommodation',
          "Properties that are not the tenant's main home",
          'Tenancies of more than seven years with no landlord break clause',
          'Student accommodation arranged directly by an educational institution',
          'Employer-provided accommodation',
          'Mobile homes',
        ],
      },
      {
        heading: 'The Four Ways to Conduct a Right to Rent Check',
        body: "As of 2026, there are four compliant methods:",
        type: 'list',
        items: [
          'Manual check: examine original hard-copy documents in person. Acceptable documents are set out in List A (permanent right to rent) and List B (time-limited right to rent) in the Home Office code of practice.',
          'Identity Document Verification Technology (IDVT): use a certified Identity Service Provider (ISP) to verify British or Irish nationals digitally. From 1 October 2026, only registered RtR DVSPs can be used for this type of check.',
          'Home Office online checking service: for tenants with a biometric residence permit or card, or a UKVI account (eVisa). The tenant provides a share code; the landlord verifies online at gov.uk.',
          'Home Office check on behalf of the landlord: available where the tenant has no acceptable identity documents. The landlord requests the Home Office to conduct the check directly.',
        ],
      },
      {
        heading: 'Understanding eVisas',
        body: "Since 2018, the government has been issuing eVisas instead of physical biometric residence permits. An eVisa is a digital immigration status held in a UKVI account. Landlords cannot check an eVisa by looking at a physical document — the only compliant method is the Home Office online checking service, using a share code provided by the tenant. If a tenant presents a physical biometric residence permit or card that has expired, do not refuse to check them — use the online service instead.",
        type: 'callout',
      },
      {
        heading: 'Permanent vs Time-Limited Right to Rent',
        body: "There are two types of right to rent:",
        type: 'list',
        items: [
          'Permanent right to rent: British and Irish nationals, those with settled status (indefinite leave to remain), and EU/EEA nationals with pre-settled status who arrived before July 2021. One check is sufficient — no follow-up required.',
          'Time-limited right to rent: tenants with a visa or limited leave to remain. You must conduct a follow-up check before their right to rent expires. A valid initial check provides a statutory excuse for at least 12 months.',
        ],
      },
      {
        heading: 'Penalties for Non-Compliance',
        body: "The penalties for failing to conduct a Right to Rent check, or for renting to someone without the right to rent, are severe:",
        type: 'warning',
        items: [
          'Civil penalty: £10,000 per occupier for a first offence; £20,000 for a repeat offence.',
          'For lodger rooms: £5,000 (first offence) or £10,000 (repeat offence).',
          'Criminal conviction: unlimited fine or up to five years in prison for knowingly renting to someone without the right to rent.',
        ],
      },
      {
        heading: 'Establishing a Statutory Excuse',
        body: "You can establish a statutory excuse — a legal defence against a civil penalty — by conducting a proper and timely Right to Rent check and retaining copies of the documents or check records. The statutory excuse protects you even if it later emerges that the tenant provided false documents, provided the check was conducted correctly.",
      },
      {
        heading: 'Document Retention',
        body: "Retain copies of all Right to Rent check documents for the duration of the tenancy and for at least one year after it ends. For digital checks, retain a record of the check date, the result, and the provider used.",
      },
      {
        heading: 'The Government Code of Practice',
        body: "The Home Office publishes a statutory Code of Practice for landlords on Right to Rent checks. It covers: how to perform checks, which occupants need checking, how to avoid discrimination, and the penalties for non-compliance. PDA Letting Agents conducts Right to Rent checks as part of our full management service — contact us if you need support.",
        type: 'tip',
      },
    ],
  },
  {
    id: 'art-006',
    slug: 'landlord-court-fees-rising-july-2026',
    title: "What the July 2026 Court Fee Rises Mean for Chester Landlords",
    excerpt:
      "Court fees for possession claims, bailiff warrants, and tribunal applications are increasing from 13 July 2026. Here's a full breakdown of the changes and what they mean for Chester landlords.",
    category: 'landlord-tips',
    author: 'PDA Legal Team',
    readTimeMin: 5,
    publishedAt: '2026-07-09T09:00:00Z',
    featured: false,
    seoTitle: "Court Fees Rising for Landlords in July 2026 | PDA Estates",
    metaDescription: "Court fees for residential possession claims rose in July 2026. PDA Estates explains what this means for Chester and Wirral landlords and why acting early matters.",
    tags: ['Court Fees', 'Possession Claims', 'Tribunal', 'Renters Rights Act', 'Property Chamber'],
    relatedSlugs: [
      'section-8-new-route-to-possession',
      'section-8-selling-or-moving-in',
      'renters-rights-act-does-not-go-far-enough',
    ],
    sections: [
      {
        body: "From 13 July 2026, court fees for landlords will increase in line with inflation. The changes affect possession claims in the County Court, bailiff fees, and a wide range of fees in the Residential Property Division of the First-tier Tribunal Property Chamber. The increases are intended to fund improvements to the justice system, including the end-to-end digitisation of the possession court process promised under the Renters' Rights Act.",
      },
      {
        heading: 'Key Fee Changes from 13 July 2026',
        body: "The most significant changes for landlords are:",
        type: 'list',
        items: [
          'Possession claims (County Court): increasing from £404 to £415.',
          'Bailiff fees (warrant of possession or delivery): increasing from £148 to £152.',
          'Appeals against local authority decisions: increased cost.',
          '80 fees in the Residential Property Division of the First-tier Tribunal Property Chamber will increase, covering leasehold and most residential property cases.',
        ],
      },
      {
        heading: 'Fees Frozen or Reduced',
        body: "Not all fees are increasing. Notably:",
        type: 'list',
        items: [
          "Fees for tenants challenging a proposed rent increase remain frozen at £47 — the hearing fee is also waived. This is a deliberate policy choice to ensure tenants can challenge rent increases without financial barriers.",
          'Fees for pitch fee determinations (mobile homes) remain at £23, with the hearing fee waived.',
          'Urgent building safety matters remain free until early 2027.',
          'Electronic Communications Code cases remain free until early 2027.',
          'Four fees have been reduced to reflect lower underlying costs.',
        ],
      },
      {
        heading: 'The New Tiered Fee Framework for the Property Chamber',
        body: "The 80 fee changes in the Residential Property Division introduce a new tiered fee framework with five tiers. Most cases will attract an application fee of £200 and a hearing fee of £300, or £114 and £227 for lower-value cases. The new framework is designed to make the fee structure more transparent and proportionate to the complexity of the case.",
        type: 'callout',
      },
      {
        heading: 'The Broader Context: 170 Fees Increasing Across the Courts',
        body: "The July 2026 changes are part of a wider package of court fee increases. In total, 170 fees across the civil courts are increasing by 2.6% in line with 2024/25 inflation. A further 27 fees are increasing by an average of 34% to account for accumulated inflation that was not passed on in previous years. The probate application fee is increasing to £526.",
      },
      {
        heading: 'Why Are Fees Increasing?',
        body: "The government has been clear that the fee increases are intended to fund improvements to the justice system, including the digitisation of the possession court process promised under the Renters' Rights Act. Housing Minister Matthew Pennycook has pledged end-to-end digitisation of the possession process by spring 2027. The current paper-based system, combined with the requirement for a court hearing in every Section 8 possession case, has created significant backlogs — with waiting times already around eight months before the Act came into force.",
      },
      {
        heading: 'What This Means for Chester Landlords',
        body: "The fee increases are modest in absolute terms — an extra £11 for a possession claim, an extra £4 for a bailiff warrant. However, they are a reminder that the cost of recovering possession under the new Section 8 regime is higher than under the old Section 21 process, both in terms of fees and time. Landlords should factor these costs into their financial planning and ensure they have robust tenancy management processes in place to minimise the risk of needing to bring possession proceedings.",
        type: 'tip',
      },
      {
        heading: 'Need Help with a Possession Claim?',
        body: "PDA Letting Agents has an in-house landlord's solicitor who can advise you on the Section 8 possession process, prepare and serve notices, and represent you in court if required. Contact us to discuss your situation — we offer a free initial consultation for Chester and North Wales landlords.",
      },
    ],
  },

  // ─── NEW ARTICLES FROM PDF (14 July 2026) ────────────────────────────────────

  {
    id: 'art-007',
    slug: 'section-8-grounds-1-and-1a-possession',
    title: "Section 8 Grounds 1 & 1a: Navigating Possession Under the Renters' Rights Act",
    excerpt:
      "Section 21 is abolished. Learn how to correctly use mandatory Section 8 Grounds 1 and 1a to regain possession, avoid the DIY trap, and prevent costly court adjournments.",
    category: 'renters-rights-act',
    author: 'PDA Legal Team',
    readTimeMin: 8,
    publishedAt: '2026-07-14T09:00:00Z',
    featured: true,
    seoTitle: "Section 8 Grounds 1 & 1a: Selling or Moving In Under the Renters' Rights Act",
    metaDescription:
      "Section 21 is abolished. Learn how to correctly use mandatory Section 8 Grounds 1 and 1a to regain possession, avoid the DIY trap, and prevent costly court adjournments.",
    tags: ['Section 8', 'Ground 1', 'Ground 1a', 'Possession', 'Renters Rights Act', 'Form 3a'],
    relatedSlugs: [
      'section-8-new-route-to-possession',
      'renters-rights-act-does-not-go-far-enough',
      'landlord-court-fees-rising-july-2026',
    ],
    sections: [
      {
        body: "Following the abolition of Section 21 on 1 May 2026 under the Renters' Rights Act, the grounds-based process under Section 8 is now your sole route to possession. The government has introduced 37 revised grounds, but for landlords looking to sell their property or move themselves (or a close family member) into it, Ground 1 and the newly introduced Ground 1a are your primary tools. As the regulatory landscape shifts, the margin for error has vanished. Here is what legally savvy landlords need to know about navigating these mandatory grounds, the evidentiary burden, and the hidden costs of getting it wrong.",
      },
      {
        heading: 'Mandatory vs. Discretionary: Knowing Your Grounds',
        body: "Under the revised Section 8 framework, it is vital to understand the distinction between the two categories of grounds:",
        type: 'list',
        items: [
          'Mandatory Grounds (Includes Grounds 1 & 1a): If you can prove to the judge that your circumstances meet the strict criteria of the ground, the court must grant a possession order. They have no legal discretion to refuse.',
          'Discretionary Grounds: The court has the power to dismiss the claim or suspend the order, even if you have proven the facts (e.g., a judge may suspend an eviction for minor rent arrears if the tenant agrees to a repayment plan).',
        ],
      },
      {
        body: "Because Grounds 1 and 1a are mandatory, your focus must be entirely on evidentiary precision. If the facts are proven, possession is guaranteed — provided the notice was served flawlessly.",
      },
      {
        heading: 'Ground 1: Moving In Yourself or with Family',
        body: "Ground 1 applies if you, or a close family member, intend to move into the property as your main home.",
        type: 'list',
        items: [
          'Who qualifies? The definition of "close family" has been expanded. It includes spouses, civil partners, cohabiting partners, parents, grandparents, siblings, children, grandchildren, and half-siblings.',
          "The Evidence: Judges will look for firm, documented evidence of your intention. This might include proof that you have sold your main residence, or that a family member's children are enrolled in a local school.",
        ],
      },
      {
        heading: 'Ground 1a: Selling the Property',
        body: "Ground 1a is a new addition to the Housing Act, allowing you to recover possession if you intend to sell the property.",
        type: 'list',
        items: [
          'The Evidence: You will need to prove genuine intent to sell. Acceptable evidence includes property valuations, instructions to an estate agent, or communications with property portals.',
        ],
      },
      {
        type: 'warning',
        body: 'Crucial Restriction: For both grounds, you cannot require the tenant to leave during the first 12 months of the tenancy. You may serve the 4-month notice during this period, but the expiry date cannot fall before the 12-month mark. Furthermore, 1 May 2026 did not "reset" the clock on existing tenancies.',
      },
      {
        heading: 'The False Economy of the DIY Possession Claim',
        body: 'Many experienced landlords ask: "I understand the grounds, so why shouldn\'t I just download the new Form 3a and serve it myself?"\n\nThe answer lies in the severe financial risks of a botched notice. Under the new regime, the evidentiary burden is high, and the prescribed wording of Form 3a is unforgiving. If you make a technical error — such as miscalculating the notice period, failing to correctly reference the legal text of the ground, or serving it while your deposit protection is non-compliant — the notice is invalid.',
      },
      {
        heading: 'The Domino Effect of a DIY Error',
        type: 'numbered-list',
        items: [
          'The Hearing: The judge will adjourn or dismiss your claim due to the technical defect.',
          'The Delay: You must start again, adding another 8 months to your court wait time.',
          'The Financial Hit: You lose the new £415 court fee, plus thousands in lost rent. Furthermore, if you are found to have knowingly or recklessly misused the ground, you face civil penalties up to £7,000, or criminal fines up to £40,000 and Rent Repayment Orders.',
        ],
      },
      {
        body: "Instructing a specialist property solicitor to draft and serve the notice correctly the first time is vastly more cost-effective than absorbing the costs of an adjourned hearing and months of lost rental income.",
      },
      {
        heading: 'Reletting Restrictions',
        body: "If your property fails to sell, or your circumstances change, you cannot advertise or physically re-let the property until 12 months from either:",
        type: 'list',
        items: [
          'The date given in the notice (four months after service); or',
          'The date the possession claim was filed with the court (whichever is later).',
        ],
      },
      {
        type: 'cta',
        ctaHeading: 'Secure Your Possession Claim with PDA Law',
        ctaBody: 'Navigating the 37 revised Section 8 grounds requires meticulous evidence and flawless execution of Form 3a. Do not risk an 8-month court delay and a wasted £415 fee on a DIY notice that could be dismissed on a technicality.\n\nInstruct PDA Law to draft and manage your Section 8 notice today. Simply select "Section 8 Notice Drafting" from the drop-down menu on our enquiry form and provide a brief summary of your situation. Our property litigation team will review your details and advise on the most cost-effective route to possession.',
        ctaLabel: 'Instruct PDA Law',
        ctaLink: '/contact',
      },
      {
        type: 'cta-crosssell',
        ctaHeading: 'Overwhelmed by the new regulatory landscape?',
        ctaBody: 'If navigating these complex grounds feels like too much hassle, let PDA Estates take over the day-to-day management and compliance of your property. Free yourself from the stress of court proceedings and DIY pitfalls.',
        ctaLabel: 'Contact PDA Estates',
        ctaLink: '/contact',
      },
    ],
  },
  {
    id: 'art-008',
    slug: 'right-to-rent-october-2026-digital-mandate',
    title: "The October 2026 Right to Rent Deadline: Why Manual Checks Are Now a Liability",
    excerpt:
      "From 1 October 2026, digital Right to Rent checks via a registered provider (RtR DVSP) become mandatory. Avoid severe civil and criminal penalties. Ensure compliance with PDA Estates.",
    category: 'landlord-tips',
    author: 'PDA Legal Team',
    readTimeMin: 6,
    publishedAt: '2026-07-14T10:00:00Z',
    featured: false,
    seoTitle: "Right to Rent Checks 2026: The October Digital Mandate & Penalties",
    metaDescription:
      "From 1 October 2026, digital Right to Rent checks via a registered provider (RtR DVSP) become mandatory. Avoid severe civil and criminal penalties. Ensure compliance with PDA Estates.",
    tags: ['Right to Rent', 'Digital Checks', 'RtR DVSP', 'Compliance', 'October 2026'],
    relatedSlugs: [
      'digital-right-to-rent-checks-october-2026',
      'right-to-rent-complete-guide',
      'section-8-new-route-to-possession',
    ],
    sections: [
      {
        body: "For experienced landlords, Right to Rent checks are a familiar pre-tenancy hurdle. However, the regulatory landscape is shifting decisively. From 1 October 2026, secondary legislation will make it mandatory to use a registered digital verification provider (a RtR DVSP) if you opt to carry out these checks electronically. While manual checks using hard-copy documents (List A or B) and Home Office online share codes remain valid, the government's clear trajectory is toward a fully digital immigration system. For landlords managing their own portfolios, navigating the nuances of List A versus List B, time-limited follow-up checks, and the new digital trust framework presents a growing compliance risk.",
      },
      {
        heading: 'The Severe Reality of Non-Compliance',
        body: "The stakes for getting this wrong are no longer just administrative. The penalties for renting to a disqualified occupier are severe:",
        type: 'list',
        items: [
          'Civil Penalties: Up to £10,000 per occupier for a first offence, rising to £20,000 for repeat offences.',
          'Criminal Prosecution: If you knowingly allow someone to occupy the property while disqualified, you face an unlimited fine or up to 5 years in prison.',
        ],
      },
      {
        body: 'While a properly executed check provides a "statutory excuse" against prosecution, the burden of proof rests entirely on you. Retaining copies, conducting timely follow-up checks for time-limited visas, and verifying that digital providers meet the Home Office trust framework are non-negotiable.',
      },
      {
        heading: 'The Hidden Cost of the "DIY" Compliance Approach',
        body: "Many landlords attempt to manage these checks in-house to save on agency fees. However, a single oversight — such as failing to conduct a follow-up check 11 months into a time-limited tenancy, or accepting an invalid share code (e.g., one beginning with 'W' or 'S' instead of 'R') — voids your statutory excuse. When a compliance failure is discovered, the resulting investigation, potential fines, and the complex \"reasonable steps\" required to lawfully terminate the tenancy far outweigh the cost of professional management.",
      },
      {
        type: 'cta',
        ctaHeading: 'Frictionless Compliance with PDA Estates',
        ctaBody: "Do not leave your portfolio exposed to criminal liability or six-figure civil penalties. Let the experts handle the burden of proof.\n\nOur specialist lettings team ensures every Right to Rent check is flawlessly executed, documented, and stored, giving you absolute peace of mind.",
        ctaLabel: 'Contact PDA Estates',
        ctaLink: '/contact',
      },
      {
        type: 'cta-crosssell',
        ctaHeading: 'Facing a Possession Issue?',
        ctaBody: "If you are already dealing with a non-compliant tenant or need to regain possession, our sister company, PDA Law, can assist.",
        ctaLabel: "Explore PDA Law's Section 8 Services",
        ctaLink: '/contact',
      },
    ],
  },
  {
    id: 'art-009',
    slug: 'court-fees-rising-july-2026-possession-claims',
    title: "Court Fees Rise on 13 July 2026: Why Getting Your Possession Claim Right the First Time is Non-Negotiable",
    excerpt:
      "County court possession claim fees rise to £415 on 13 July 2026. With 8-month wait times, a flawed DIY Section 8 notice is a costly risk. Instruct PDA Law.",
    category: 'landlord-tips',
    author: 'PDA Legal Team',
    readTimeMin: 6,
    publishedAt: '2026-07-14T11:00:00Z',
    featured: false,
    seoTitle: "Court Fees Rising for Possession Claims — July 2026 | PDA Estates",
    metaDescription:
      "Court fees for residential possession claims rose in July 2026. PDA Estates explains what this means for Chester and Wirral landlords and why acting early matters.",
    tags: ['Court Fees', 'Possession Claims', 'Section 8', 'Form 3a', 'Renters Rights Act'],
    relatedSlugs: [
      'section-8-selling-or-moving-in',
      'section-8-new-route-to-possession',
      'landlord-court-fees-rising-july-2026',
    ],
    sections: [
      {
        body: "As of 13 July 2026, HM Courts & Tribunals Service has implemented inflation-linked fee increases. For landlords seeking to repossess their properties, the fee for a County Court possession claim has risen from £404 to £415, and the fee for a warrant of possession (bailiffs) has increased from £148 to £152.\n\nWhile an £11 increase may seem marginal, it must be viewed in the context of the current justice system. With court wait times already stretching to eight months and the paper-based process abolished under the Renters' Rights Act, every possession claim now requires a court hearing.",
      },
      {
        heading: 'What the Fee Increase Means in Numbers',
        body: "To be precise about the new fee schedule: a standard County Court possession claim now costs £415 (up from £404), and this applies to both Section 21 and Section 8 claims. If the claim proceeds to enforcement, the warrant of possession fee adds a further £152 (up from £148). In total, a landlord pursuing possession through to bailiff enforcement now faces upfront court costs of £567 before any legal fees are considered.\n\nFor landlords who self-manage, these figures must be factored into the true cost of a problem tenancy. A single month of non-payment at average Chester rents (around £850 per month for a one-bedroom property) already exceeds the court fee — but the real financial exposure comes from the timeline, not the fee itself.",
      },
      {
        heading: 'Timeline Impact: Why Upfront Costs Are Only Part of the Picture',
        body: "Possession proceedings were already taking 6 to 12 months through the courts before the July 2026 fee increase. That timeline has not improved. Under the Renters' Rights Act, the abolition of Section 21 means every possession claim must be argued on its merits at a hearing — there is no longer a straightforward administrative route.\n\nThe financial risk of delayed action is therefore compounded. A landlord who files a flawed DIY Section 8 notice faces not just the loss of the £415 court fee when the claim is dismissed, but a further 8-month wait before a corrected claim can be heard. At £850 per month in lost or withheld rent, that delay costs approximately £6,800 — on top of the wasted court fee and the cost of instructing a solicitor to start again.\n\nHigher upfront costs make it more important than ever to get the claim right the first time.",
      },
      {
        heading: 'The True Cost of a Flawed DIY Notice',
        body: "In this environment, time is your most valuable asset. Many legally savvy landlords attempt to draft and serve their own Section 8 notices using the new mandatory Form 3a. However, the revised 37 grounds come with strict evidentiary burdens and precise caveats.\n\nIf your DIY notice contains a technical error — such as miscalculating the 4-month notice period, failing to include the exact mandated legal text, or serving it while your deposit protection is non-compliant — the notice is invalid.",
      },
      {
        heading: 'The Domino Effect of an Invalid Notice',
        type: 'numbered-list',
        items: [
          'The judge will adjourn or dismiss your claim at the hearing.',
          'You must restart the process, adding another 8 months to your wait time.',
          'You lose the new £415 court fee, plus thousands in lost rental income, and will inevitably have to instruct a solicitor at a higher cost to rectify the situation.',
        ],
      },
      {
        type: 'warning',
        body: "Furthermore, knowingly or recklessly misusing a possession ground (such as Ground 1 or 1a) can result in civil penalties up to £7,000, criminal fines up to £40,000, and Rent Repayment Orders.",
      },
      {
        heading: 'Practical Advice for Landlords',
        body: "Landlords who self-manage should factor the new court fee schedule into their risk calculations when assessing a problem tenancy. The cost of a possession claim is no longer just a legal fee — it is a significant financial commitment that, if wasted on a flawed notice, doubles the total cost and timeline.\n\nA fully managed service changes this equation entirely. When PDA Estates manages your property, we handle the entire process: monitoring rent payments, issuing compliant notices at the correct stage, and instructing our in-house legal team (PDA Law) to manage any possession proceedings. You do not pay referral fees to an external firm, and you benefit from a team that knows your tenancy file from day one.\n\nFor landlords currently self-managing who are facing a difficult tenancy, this is the moment to consider whether the cost of professional management is lower than the cost of getting it wrong.",
      },
      {
        type: 'cta',
        ctaHeading: 'Secure Your Claim with PDA Law',
        ctaBody: 'Do not risk an 8-month delay and a wasted £415 fee on a technicality. Instruct PDA Law to draft and manage your Section 8 possession claim today. Simply select "Section 8 Notice Drafting" from the drop-down menu on our enquiry form and provide a brief summary of your situation. Our property litigation team will review your details and advise on the most cost-effective route to possession.',
        ctaLabel: 'Instruct PDA Law',
        ctaLink: '/contact',
      },
      {
        type: 'cta-crosssell',
        ctaHeading: 'Overwhelmed by the new regulatory landscape?',
        ctaBody: 'If navigating these complex grounds feels like too much hassle, let PDA Estates take over the day-to-day management and compliance of your property. Free yourself from the stress of court proceedings and DIY pitfalls.',
        ctaLabel: 'Explore Our Landlord Services',
        ctaLink: '/landlord-services',
      },
    ],
  },
  {
    id: 'art-010',
    slug: 'future-landlord-regulations-awaabs-law-prs-database',
    title: "Beyond the Renters' Rights Act: Awaab's Law, the PRS Database, and the Next Wave of Regulation",
    excerpt:
      "The HCLG Committee signals stricter enforcement, Awaab's Law in the PRS, and a comprehensive PRS Database. Future-proof your portfolio and avoid reactive penalties.",
    category: 'renters-rights-act',
    author: 'PDA Legal Team',
    readTimeMin: 7,
    publishedAt: '2026-07-14T12:00:00Z',
    featured: false,
    seoTitle: "Future Landlord Regulations 2026: Awaab's Law, PRS Database & Decent Homes",
    metaDescription:
      "The HCLG Committee signals stricter enforcement, Awaab's Law in the PRS, and a comprehensive PRS Database. Future-proof your portfolio and avoid reactive penalties.",
    tags: ["Awaab's Law", 'PRS Database', 'Decent Homes Standard', 'Local Licensing', 'Future Regulation'],
    relatedSlugs: [
      'renters-rights-act-does-not-go-far-enough',
      'section-8-new-route-to-possession',
      'landlord-court-fees-rising-july-2026',
    ],
    sections: [
      {
        body: "If you thought the abolition of Section 21 and the introduction of the 37 revised Section 8 grounds under the Renters' Rights Act (May 2026) was the culmination of property regulation, the recent Housing, Communities and Local Government (HCLG) Committee report suggests otherwise.\n\nThe cross-party committee has officially concluded that the current reforms \"do not go far enough\" to protect tenants. For legally savvy landlords, this report is a clear warning: the regulatory ceiling has not been reached. More aggressive enforcement, stricter compliance timelines, and sweeping new data requirements are on the horizon.\n\nHere is what you need to know about the incoming regulatory shifts and how to future-proof your portfolio against the looming compliance trap.",
      },
      {
        heading: "1. Awaab's Law Comes to the Private Rented Sector",
        body: "Originally introduced for social housing, the committee is pushing for Awaab's Law to be rolled out in the private rented sector (PRS) this year, with full legal timescales for resolving hazards in place by 2028/29.",
        type: 'list',
        items: [
          "What it means: Landlords will be legally bound by strict, enforceable deadlines to investigate and resolve hazards such as damp, mould, and poor ventilation once reported.",
          "The Risk: Failing to meet these statutory timescales will likely expose landlords to severe civil penalties and Rent Repayment Orders (RROs). The era of \"managing maintenance at your own pace\" is ending.",
        ],
      },
      {
        heading: '2. The PRS Database: From Registry to Reputation Tracker',
        body: "The government is developing the Private Rented Sector (PRS) Database, but the HCLG Committee wants it to be far more than a simple registry of properties. They recommend it include \"comprehensive information on the fitness of landlords, the quality of homes,\" and a direct mechanism for tenants to report issues.",
        type: 'list',
        items: [
          "The Reality: AI and government algorithms will soon cross-reference your property's quality metrics, your compliance history, and tenant complaints.",
          'The Risk: A poor compliance record on the database could not only result in proactive local authority inspections but could also impact your ability to secure future mortgages or insurance.',
        ],
      },
      {
        heading: '3. Aggressive Local Authority Enforcement & Licensing',
        body: "The committee has highlighted a glaring flaw in the current system: between 2023 and 2025, local authorities collected just 25% of the £30 million in civil penalties imposed on landlords. The government views this as unacceptable.",
        type: 'list',
        items: [
          'The Shift: Local authorities are being pressured to become proactive, not reactive. Expect a surge in unannounced inspections and aggressive enforcement of the Decent Homes Standard (ahead of the 2035 deadline).',
          'The Licensing Threat: Despite industry pushback, the committee wants to make it easier for councils to introduce local licensing schemes. If your local authority implements a blanket licensing scheme, the administrative and financial burden on self-managing landlords will multiply exponentially.',
        ],
      },
      {
        heading: 'The "Reactive Compliance" Trap',
        type: 'warning',
        body: "Many landlords adopt a reactive approach to compliance: waiting for a tenant complaint, a council notice, or a court order before taking action. Under the new regulatory paradigm, this is a catastrophic financial strategy. When local authorities are armed with enhanced enforcement powers and a comprehensive PRS Database, a reactive approach guarantees that you will face the maximum penalties. The cost of proactively auditing your portfolio for Decent Homes compliance, ensuring rapid maintenance response times, and navigating complex local licensing schemes is a fraction of the cost of an RRO, a £20,000 civil penalty, or a criminal prosecution.",
      },
      {
        heading: 'Future-Proof Your Portfolio with PDA',
        body: "Do not wait for the 2028/29 Awaab's Law deadlines or the full rollout of the PRS Database to discover your portfolio is non-compliant. Take proactive control of your regulatory risk today.",
      },
      {
        type: 'cta',
        ctaHeading: 'Instruct PDA Law for Compliance Auditing',
        ctaBody: "Instruct PDA Law to audit your portfolio's compliance and advise on local licensing and enforcement defence. Our property litigation and compliance team ensures your legal obligations are met, protecting you from aggressive local authority enforcement and costly tribunal claims.",
        ctaLabel: 'Speak to PDA Law',
        ctaLink: '/contact',
      },
      {
        heading: 'The Ultimate Escape from Regulatory Hassle',
        body: "If the prospect of tracking Awaab's Law deadlines, managing the PRS Database, and dealing with local licensing schemes sounds like a burden you no longer wish to carry, let us take it off your hands entirely.",
      },
      {
        type: 'cta-crosssell',
        ctaHeading: 'Transition to Fully Managed with PDA Estates',
        ctaBody: "We handle the maintenance, the compliance, the database reporting, and the tenant communications, ensuring your portfolio is always future-proofed and fully compliant.",
        ctaLabel: 'Contact PDA Estates',
        ctaLink: '/contact',
      },
    ],
  },
];
